import i18next, { type TOptions } from "i18next";
import { defineStore } from "pinia";

import { Language } from "~/utils/constants";

import { type State } from "./types";

export const useLanguageStore = defineStore("language", {
  state: (): State => ({
    currentLanguage: null,
  }),

  getters: {
    language(state): Language {
      return state.currentLanguage ?? Language.EN;
    },

    /**
     * Get the translation for the given key.
     *
     * This getter is designed as a workaround for handling the reactivity
     * issue of the i18next.t function. It ensures that changes in the
     * language state trigger the re-rendering of the component.
     */
    t:
      (state) =>
      (key: string, object: TOptions = {}) => {
        return i18next.t(key, { ...object, lng: state.currentLanguage as string });
      },

    isFrench(state): boolean {
      return state.currentLanguage === Language.FR;
    },
  },

  actions: {
    async setCurrentLanguage(language: Language): Promise<void> {
      await Promise.all([this._updateStoreLanguage(language), this._updateRouteLanguage(language)]);
    },

    async _updateStoreLanguage(language: Language): Promise<void> {
      await i18next.changeLanguage(language);
      this.currentLanguage = (i18next.language as Language) ?? Language.EN;
    },

    /**
     * Update the language parameter in the route.
     */
    async _updateRouteLanguage(language: Language): Promise<void> {
      const route = useRoute();
      await navigateTo(
        {
          ...route,
          // @ts-expect-error for some reason the params are not typed correctly. Nuxt issue?
          params: { ...route.params, language },
        },
        { replace: true },
      );
    },

    /**
     * Get the localized field from an object.
     *
     * Example:
     * getLocalizedFieldData({ name_en: "Name", name_fr: "Nom" }, "name") => "Name"
     *
     * @param object - The object to get the localized field from.
     * @param baseField - The base field name.
     * @param defaultValue - The default value to return if the field is not found.
     */
    getLocalizedFieldData(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      object: Record<string, any>,
      baseField: string,
      defaultValue?: string,
    ): string {
      const language = this.currentLanguage ?? Language.EN;
      const field = `${baseField}_${language}`;
      return object?.[field] || defaultValue;
    },
  },
});
